import type { paths } from "@/api-schema";
import { RelocationOrderFlowAction } from "@/common/api/relocation-order-flow/_helpers/resolve-relocation-order-flow-path";
import { useRelocationOrderFlowMutation } from "@/common/api/relocation-order-flow/use-relocation-order-flow.mutation";
import { useRelocationOrderByUuidQuery } from "@/common/api/relocation-order/use-relocation-order-by-uuid.query";
import { dateToInputString, inputStringToDate, parseApiDateOnly } from "@/common/helpers/date-input";
import { formatDateToApi } from "@/common/helpers/format-date";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Form } from "@/components/ui/form";
import { FormCard } from "@/components/ui/form-card";
import { Skeleton } from "@/components/ui/skeleton";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "wouter";
import { navigate } from "wouter/use-browser-location";
import { formSchema, formSchemaType, NewDateFormFields } from "./new-date-form-fields";

interface NewDatePageParams {
  uuid: string;
}

interface NewDatePageProps {
  flowAction: RelocationOrderFlowAction;
  backTo: string;
  navigateToOnSuccess: string;
}

export function NewDatePage({ flowAction, backTo, navigateToOnSuccess }: NewDatePageProps) {
  const { t } = useTranslation(["fit", "common"]);
  const params = useParams<NewDatePageParams>();
  const uuid = params.uuid;

  return (
    <UuidExistenceWrapper<
      paths["/v1/relocation-order/{uuid}"]["get"]["parameters"]["query"],
      paths["/v1/relocation-order/{uuid}"]["get"]["responses"][200]["content"]["application/json"]
    >
      uuid={uuid}
      query={useRelocationOrderByUuidQuery}
      notFoundProps={{
        title: t("fit:RelocationOrderNotFound"),
        message: t("fit:RequestedOrderDoesNotExist"),
        description: t("fit:PleaseCheckOrderIdOrContactSupport"),
        backTo,
        backToLabel: t("fit:BackToOrderOverview"),
      }}
    >
      <NewDatePageContent
        relocationOrderUuid={uuid}
        flowAction={flowAction}
        navigateToOnSuccess={navigateToOnSuccess}
      />
    </UuidExistenceWrapper>
  );
}

interface NewDatePageContentProps {
  relocationOrderUuid: string;
  flowAction: RelocationOrderFlowAction;
  navigateToOnSuccess: string;
}

function NewDatePageContent({ relocationOrderUuid, flowAction, navigateToOnSuccess }: NewDatePageContentProps) {
  const { t } = useTranslation(["fit", "common"]);
  const pageTitle = t("fit:SetPickupDate");
  const subtitle = t("common:ReviewAndSaveChangesDescription");

  const { data: relocationOrderData, isPending: relocationOrderIsLoading } =
    useRelocationOrderByUuidQuery(relocationOrderUuid);
  const { isPending: setPickupDateIsPending, mutateAsync: setPickupDateMutation } =
    useRelocationOrderFlowMutation(flowAction);

  const form = useForm<formSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newEarliestPickupDate: relocationOrderData?.earliestPickupDate
        ? dateToInputString(parseApiDateOnly(relocationOrderData.earliestPickupDate))
        : "",
    },
  });

  const newEarliestPickupDateValue = useWatch({
    control: form.control,
    name: "newEarliestPickupDate",
  });

  const hasDateChanged = relocationOrderData?.earliestPickupDate
    ? dateToInputString(parseApiDateOnly(relocationOrderData.earliestPickupDate)) !== newEarliestPickupDateValue
    : !!newEarliestPickupDateValue;

  useEffect(() => {
    if (relocationOrderData?.earliestPickupDate) {
      form.setValue(
        "newEarliestPickupDate",
        dateToInputString(parseApiDateOnly(relocationOrderData.earliestPickupDate)),
      );
    }
  }, [relocationOrderData, form]);

  const onSubmit = async (values: formSchemaType) => {
    if (!relocationOrderData) return;

    const { newEarliestPickupDate } = values;

    await toastMutationStatus({
      mutationFn: () =>
        setPickupDateMutation({
          relocationOrder: relocationOrderData,
          body: {
            earliestPickupDate: formatDateToApi(inputStringToDate(newEarliestPickupDate)!),
          },
        }),
      successMessage: t("fit:PickupDateSet"),
      errorMessage: t("fit:ErrorSettingPickupDate"),
      onSuccess: () => {
        navigate(navigateToOnSuccess);
      },
    });
  };

  if (relocationOrderIsLoading) {
    return (
      <CenteredLayout variant={CenteredLayoutVariant.Form} title={pageTitle} subTitle={subtitle}>
        <FormCard title={pageTitle} titleHidden>
          <div className="space-y-8">
            <Skeleton className="h-8 w-72" />

            <div className="flex flex-col gap-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-64" />
            </div>

            <div className="flex gap-2">
              <Skeleton className="h-10 w-20" />
              <Skeleton className="h-10 w-20" />
            </div>
          </div>
        </FormCard>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout variant={CenteredLayoutVariant.Form} title={pageTitle} subTitle={subtitle}>
      <FormCard title={pageTitle} titleHidden>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <NewDateFormFields
              isLoading={setPickupDateIsPending}
              formControl={form.control}
              form={form}
              disabled={!hasDateChanged}
              cancelNavigateTo={navigateToOnSuccess}
            />
          </form>
        </Form>
      </FormCard>
    </CenteredLayout>
  );
}
